import React from 'react';
import { useParams } from 'react-router-dom';
import SectionHeading from '../components/ui/SectionHeading';

const legalContent = {
  'privacy-policy': {
    title: 'Privacy Policy',
    content: `
      <h2>1. Information We Collect</h2>
      <p>We collect information that you provide directly to us, including:</p>
      <ul>
        <li>Contact information (name, email, phone number)</li>
        <li>Property information</li>
        <li>Communication preferences</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide and improve our services</li>
        <li>Communicate with you</li>
        <li>Send you marketing communications (with your consent)</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2>3. Information Sharing</h2>
      <p>We do not sell your personal information. We may share your information with:</p>
      <ul>
        <li>Service providers who assist in our operations</li>
        <li>Professional advisors</li>
        <li>Law enforcement when required by law</li>
      </ul>

      <h2>4. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access your personal information</li>
        <li>Correct inaccurate information</li>
        <li>Request deletion of your information</li>
        <li>Opt-out of marketing communications</li>
      </ul>
    `
  },
  'terms-of-service': {
    title: 'Terms of Service',
    content: `
      <h2>1. Acceptance of Terms</h2>
      <p>By accessing or using RentSparks services, you agree to be bound by these Terms of Service.</p>

      <h2>2. Services</h2>
      <p>RentSparks provides property management services including:</p>
      <ul>
        <li>Tenant screening and placement</li>
        <li>Rent collection</li>
        <li>Property maintenance</li>
        <li>Financial reporting</li>
      </ul>

      <h2>3. Client Responsibilities</h2>
      <p>As a client, you agree to:</p>
      <ul>
        <li>Provide accurate information</li>
        <li>Maintain required insurance</li>
        <li>Comply with all applicable laws</li>
        <li>Pay agreed-upon fees</li>
      </ul>

      <h2>4. Limitation of Liability</h2>
      <p>RentSparks shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services.</p>
    `
  },
  'license': {
    title: 'License Information',
    content: `
      <h2>Professional Licensing</h2>
      <p>RentSparks maintains all required licenses and certifications:</p>
      <ul>
        <li>California Department of Real Estate (DRE) License #02123456</li>
        <li>Certified Property Manager (CPM®)</li>
        <li>National Association of Residential Property Managers (NARPM®)</li>
      </ul>

      <h2>Service Area</h2>
      <p>We are licensed to operate in:</p>
      <ul>
        <li>Los Angeles County</li>
        <li>Orange County</li>
        <li>San Bernardino County</li>
        <li>Ventura County</li>
      </ul>

      <h2>Professional Affiliations</h2>
      <p>RentSparks is proud to be affiliated with:</p>
      <ul>
        <li>National Association of Realtors®</li>
        <li>California Association of Realtors®</li>
        <li>Institute of Real Estate Management</li>
      </ul>
    `
  }
};

const LegalPage: React.FC = () => {
  const { page } = useParams<{ page: keyof typeof legalContent }>();
  const content = page ? legalContent[page] : null;

  if (!content) {
    return (
      <div className="py-20">
        <div className="container mx-auto px-4">
          <h1>Page not found</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-[#FAFAF5]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <SectionHeading title={content.title} centered={false} />
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: content.content }}
          />
        </div>
      </div>
    </div>
  );
};

export default LegalPage;